// MACHINERY V2 (Stroje) — "which machine is this page about, and did the fetch
// for it succeed" (M2.6 review C7).
//
// Split out of SrCreateV2.tsx, which reached 305 lines with this inline — the
// 300-line cap says split BEFORE the lines land. That page owns the chrome, the
// layout and the form; this owns one question about one id.
//
// >>> IT ANSWERS A QUESTION THE SHARED SLICE CANNOT. <<<
// `customerMachinery.machine` is an APP-WIDE slot: the fleet writes it, the live
// v1 pages write it, and a previous profile visit leaves its own machine there.
// Two facts are therefore missing from it and are kept here instead:
//   1. "HAS THIS ID BEEN FETCHED AT ALL?" On the first render of an in-place
//      `:id` change the slot still holds the PREVIOUS machine and the slice-wide
//      `loading` is still false, because the dispatch happens in a passive
//      effect after commit. Without a per-id outcome a page cannot tell "not
//      loaded yet" from "loaded and empty".
//   2. "DID IT FAIL?" `getMachine.rejected` writes `error` and clears `loading`
//      (customerMachinerySlice.ts:199-202) — it does NOT clear `machine`. A page
//      reading the slot alone shows a loading notice for ever after a rejected
//      fetch. `failed` is what lets the caller offer RetryNotice instead.
//
// A MONOTONIC REQUEST TOKEN IGNORES STALE FULFILMENTS. Machine A -> B with A
// landing last would otherwise let A's outcome overwrite B's; the token is the
// mechanism MachineProfileV2.tsx:102-123 introduced at the M2.4 review, for the
// same reason. The hook also keeps its OWN fulfilment as the fallback machine,
// so B still renders B when the shared slot was clobbered by A.
//
// The slice still WINS whenever it holds this id — that is what keeps a
// post-mutation refresh repainting the page.
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { getMachine } from "../../customerMachinerySlice";
import { Machine } from "../../models/models";

export interface SrMachineFetch {
  /** The machine this route is about; undefined until that is known. */
  current?: Machine;
  /** THIS id's own fetch has settled — whatever it produced. */
  settled: boolean;
  /** It settled by REJECTING. Offer a retry; do NOT say "not found". */
  failed: boolean;
  /** Re-runs the fetch. Safe to hand straight to RetryNotice. */
  reload: () => void;
}

export const useSrMachineFetch = (machineId: string): SrMachineFetch => {
  const dispatch = useAppDispatch();
  const machine = useAppSelector((s) => s.customerMachinery.machine);

  const [fetched, setFetched] = useState<{
    id: string;
    machine: Machine | null;
    failed: boolean;
  } | null>(null);
  const requestRef = useRef(0);

  const reload = useCallback(() => {
    if (!machineId) return;
    const token = ++requestRef.current;
    setFetched(null);
    dispatch(getMachine(machineId))
      .unwrap()
      .then((m) => {
        if (requestRef.current === token) {
          setFetched({ id: machineId, machine: m || null, failed: false });
        }
      })
      .catch(() => {
        if (requestRef.current === token) {
          setFetched({ id: machineId, machine: null, failed: true });
        }
      });
  }, [dispatch, machineId]);

  useEffect(reload, [reload]);

  const current =
    machine && machine.id === machineId
      ? machine
      : fetched && fetched.id === machineId && fetched.machine
      ? fetched.machine
      : undefined;

  const settled = !!fetched && fetched.id === machineId;

  return { current, settled, failed: settled && !!fetched && fetched.failed, reload };
};

export default useSrMachineFetch;
